import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, X, User, ChevronDown } from 'lucide-react';
import api from '../utils/api';

/**
 * UserSearchDropdown — Reusable component for searching and selecting a user.
 * Used for manager, team lead, buddy, HR contact assignments.
 *
 * Props:
 * - label: string — field label
 * - value: { id, first_name, last_name, email, position } | null — selected user
 * - onChange: (user | null) => void — callback when selection changes
 * - placeholder: string — search input placeholder
 * - excludeIds: number[] — user IDs to exclude from results
 * - helpText: string — optional helper text below the field
 */
const UserSearchDropdown = ({
    label,
    value,
    onChange,
    placeholder = 'Search by name or email...',
    excludeIds = [],
    helpText,
}) => {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [isOpen, setIsOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const containerRef = useRef(null);
    const debounceRef = useRef(null);

    // Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Debounced search
    const searchUsers = useCallback(async (searchQuery) => {
        if (!searchQuery || searchQuery.trim().length < 1) {
            setResults([]);
            return;
        }

        setLoading(true);
        try {
            const response = await api.get(`/employees/search?q=${encodeURIComponent(searchQuery.trim())}`);
            const filtered = (response.data.users || []).filter(u => !excludeIds.includes(u.id));
            setResults(filtered);
        } catch (error) {
            console.error('User search error:', error);
            setResults([]);
        } finally {
            setLoading(false);
        }
    }, [excludeIds]);

    const handleInputChange = (e) => {
        const val = e.target.value;
        setQuery(val);
        setIsOpen(true);

        // Debounce the search
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => searchUsers(val), 300);
    };

    const handleSelect = (user) => {
        onChange(user);
        setQuery('');
        setResults([]);
        setIsOpen(false);
    };

    const handleClear = () => {
        onChange(null);
        setQuery('');
        setResults([]);
    };

    const handleFocus = () => {
        setIsOpen(true);
        if (query.trim().length >= 1) {
            searchUsers(query);
        }
    };

    return (
        <div ref={containerRef} className="relative">
            {label && (
                <label className="block text-sm font-medium text-foreground mb-1.5">
                    {label}
                </label>
            )}

            {value ? (
                /* Selected user chip */
                <div
                    className="flex items-center gap-3 px-3 py-2.5 bg-secondary rounded-xl border border-border"
                >
                    <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-primary text-sm font-semibold">
                        {value.first_name?.[0]}{value.last_name?.[0]}
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">
                            {value.first_name} {value.last_name}
                        </p>
                        <p className="text-xs text-muted-foreground truncate">{value.email}</p>
                    </div>
                    <button
                        type="button"
                        onClick={handleClear}
                        className="p-1 hover:bg-destructive/10 rounded-md transition-colors"
                        title="Clear selection"
                    >
                        <X size={16} className="text-muted-foreground hover:text-destructive" />
                    </button>
                </div>
            ) : (
                /* Search input */
                <div className="relative">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input
                        type="text"
                        value={query}
                        onChange={handleInputChange}
                        onFocus={handleFocus}
                        placeholder={placeholder}
                        className="w-full pl-9 pr-8 py-2.5 bg-background border border-border rounded-xl text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent transition-colors"
                    />
                    {loading && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                            <div className="w-4 h-4 border-2 border-accent/30 border-t-accent rounded-full animate-spin" />
                        </div>
                    )}
                </div>
            )}

            {/* Dropdown results */}
            {isOpen && !value && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-card border border-border rounded-xl shadow-lg z-50 max-h-56 overflow-y-auto">
                    {loading && results.length === 0 && (
                        <div className="px-4 py-3 text-sm text-muted-foreground text-center">
                            Searching...
                        </div>
                    )}

                    {!loading && query.trim().length >= 1 && results.length === 0 && (
                        <div className="px-4 py-3 text-sm text-muted-foreground text-center">
                            No users found
                        </div>
                    )}

                    {!loading && query.trim().length < 1 && (
                        <div className="px-4 py-3 text-sm text-muted-foreground text-center">
                            Type to search...
                        </div>
                    )}

                    {results.map((user) => (
                        <button
                            key={user.id}
                            type="button"
                            onClick={() => handleSelect(user)}
                            className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-secondary transition-colors text-left"
                        >
                            <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-primary text-xs font-semibold flex-shrink-0">
                                {user.first_name?.[0]}{user.last_name?.[0]}
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-foreground truncate">
                                    {user.first_name} {user.last_name}
                                </p>
                                <p className="text-xs text-muted-foreground truncate">
                                    {user.email}{user.position ? ` · ${user.position}` : ''}
                                </p>
                            </div>
                        </button>
                    ))}
                </div>
            )}

            {helpText && (
                <p className="mt-1 text-xs text-muted-foreground">{helpText}</p>
            )}
        </div>
    );
};

export default UserSearchDropdown;
